<template>
  <v-select
    :value="value"
    :options="options"
    :reduce="(job) => job.jobnumber"
    label="label"
    :filter-by="filterJob"
    :clearable="false"
    :disabled="disabled"
    append-to-body
    :calculate-position="positionJobMenu"
    placeholder="Search job, contact, lab…"
    @input="$emit('input', $event)"
  >
    <template #option="job">
      <div class="job-option">
        <div class="job-option-title">
          <b>{{ job.jobnumber }}</b><span v-if="job.projectName"> · {{ job.projectName }}</span>
        </div>
        <div class="job-option-meta">
          {{ job.contact || 'No contact' }}<span v-if="job.lab"> · {{ job.lab }}</span><span v-if="job.date"> · {{ formatDate(job.date) }}</span>
          <span class="job-status" :class="{ done: !job.active }">{{ statusLabel(job.process) }}</span>
        </div>
      </div>
    </template>
    <template #selected-option="job">
      <span class="job-selected" :title="job.projectName">
        <b>{{ job.jobnumber || job.label }}</b><span v-if="job.contact"> – {{ job.contact }}</span>
      </span>
    </template>
    <template #no-options>
      No matching jobs.<span v-if="!showAllJobs"> Turn on “Show completed jobs” to search older ones.</span>
    </template>
  </v-select>
</template>

<script>
import vSelect from 'vue-select';
import { parseYmd } from './timesheetDates';

const STATUS_LABELS = {
  draft: 'Draft',
  ponumber: 'Awaiting PO',
  quote: 'Quote',
  shop: 'In shop',
  certification: 'Certification',
  completed: 'Completed',
  receipt_confirmed: 'Receipt confirmed',
};

// Job picker shared by the timesheet's hours and materials rows.
export default {
  name: 'JobSelect',
  components: { vSelect },

  props: {
    // The selected job number.
    value: { type: [Number, String], default: null },
    jobs: { type: Array, default: () => [] },
    showAllJobs: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },

  computed: {
    // Completed jobs stay hidden unless asked for, but the selected one is always listed.
    options() {
      return this.jobs.filter((job) => this.showAllJobs || job.active || job.jobnumber === this.value);
    },
  },

  methods: {
    filterJob(job, label, search) {
      const text = [job.jobnumber, job.projectName, job.contact, job.lab].filter(Boolean).join(' ').toLowerCase();
      return search.toLowerCase().split(/\s+/).every((word) => text.includes(word));
    },

    // The menu is wider than the cell so the job details fit.
    positionJobMenu(dropdownList, component, { width, top, left }) {
      dropdownList.style.top = top;
      dropdownList.style.left = left;
      dropdownList.style.width = `max(${width}, 380px)`;
    },

    statusLabel(process) {
      return STATUS_LABELS[process] || process || '';
    },

    formatDate(value) {
      return value ? parseYmd(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
    },
  },
};
</script>

<style lang="scss" scoped>
.job-selected {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style lang="scss">
/* The job menu is rendered at the end of <body>, outside the scoped styles. */
.vs__dropdown-menu .job-option {
  white-space: normal;
  line-height: 1.3;
  padding: 2px 0;
}

.vs__dropdown-menu .job-option-title {
  font-size: 13px;
}

.vs__dropdown-menu .job-option-meta {
  font-size: 11px;
  opacity: 0.75;
}

.vs__dropdown-menu .job-status {
  display: inline-block;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba(40, 199, 111, 0.15);
  color: #1f9d57;
  font-weight: 600;
}

.vs__dropdown-menu .job-status.done {
  background: rgba(130, 134, 139, 0.15);
  color: #6e6b7b;
}

.vs__dropdown-option--highlight .job-status {
  background: rgba(255, 255, 255, 0.25);
  color: inherit;
}
</style>
